import { normalizeLocale, type SupportedLocale } from '@/i18n/config';
import {
  getWorkspaceErrorCopy,
  type WorkspaceErrorId,
} from '@/i18n/workspace-error-copy';
import { getSendAttachmentLimitMessage } from '@/i18n/send-attachment-limit-copy';

/**
 * Backend error codes and messages that name the organization, keyed by the
 * normalized text (trimmed, lowercased, no trailing period). The backend keeps
 * "organization"; the UI says "Workspace".
 */
const WORKSPACE_ERROR_IDS: Record<string, WorkspaceErrorId> = {
  organization_not_found: 'notFound',
  org_not_found: 'notFound',
  'organization not found': 'notFound',
  'organization not found for product': 'notFound',
  'organization not found or access denied': 'notFoundOrDenied',
  org_access_denied: 'accessDenied',
  'organization access denied': 'accessDenied',
  org_membership_required: 'notMember',
  'access denied - not a member of this organization': 'notMember',
  'you are not a member of this organization': 'notMember',
  org_admin_required: 'adminRequired',
  organization_slug_taken: 'slugUnavailable',
  organization_slug_reserved: 'slugUnavailable',
  organization_slug_immutable: 'slugImmutable',
  organization_deletion_in_progress: 'deletionInProgress',
  organization_required: 'workspaceRequired',
  'organization context required': 'workspaceRequired',
  'organization context missing': 'workspaceRequired',
  'organization context not found': 'workspaceRequired',
  'organization slug is required': 'workspaceRequired',
  'could not resolve organization. please ensure you are in an organization context':
    'workspaceRequired',
  'asset does not belong to the specified organization': 'otherWorkspace',
  'asset does not belong to the provided organization': 'otherWorkspace',
  'thread asset does not belong to this organization': 'otherWorkspace',
  'import job does not belong to this organization': 'otherWorkspace',
  'organization mismatch': 'otherWorkspace',
  'mismatched organization context': 'otherWorkspace',
  member_not_in_org: 'memberNotInWorkspace',
  user_not_member_of_org: 'userNotMember',
};

/** The page's locale from `<html lang>`; the default locale during SSR. */
export function getDocumentLocale(): SupportedLocale {
  if (typeof document === 'undefined') return normalizeLocale(undefined);
  return normalizeLocale(document.documentElement?.lang);
}

function normalizeErrorKey(message: string): string {
  return message.trim().replace(/\.+$/, '').toLowerCase();
}

/** The copy id for a known organization error code or message, if any. */
export function getWorkspaceErrorId(
  message: string
): WorkspaceErrorId | undefined {
  return WORKSPACE_ERROR_IDS[normalizeErrorKey(message)];
}

function matchCase(source: string, replacement: string): string {
  if (source === source.toUpperCase()) return replacement.toUpperCase();
  if (source[0] === source[0]?.toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

/**
 * Rewrites the word "organization" to "workspace" in English prose, keeping
 * case and fixing "an organization" to "a workspace". Underscore codes such
 * as `ORGANIZATION_NOT_FOUND` are left alone because `\b` does not split on
 * `_`.
 */
export function rewriteOrganizationWording(message: string): string {
  return message
    .replace(/\b(an)(\s+)(?=organi[sz]ation)/gi, (_, article, space) => {
      return `${matchCase(article, 'a')}${space}`;
    })
    .replace(/\b(organi[sz]ation)(s?)\b/gi, (_, noun: string, plural) => {
      return `${matchCase(noun, 'workspace')}${plural}`;
    });
}

/**
 * User-facing text for a backend error message: localized workspace copy
 * when the code or message is known, otherwise the message with
 * "organization" reworded to "workspace".
 */
export function localizeWorkspaceErrorMessage(
  message: string,
  locale: string | null | undefined
): string {
  const sendLimit = getSendAttachmentLimitMessage(message, locale);
  if (sendLimit) return sendLimit;
  const id = getWorkspaceErrorId(message);
  if (id) return getWorkspaceErrorCopy(id, normalizeLocale(locale));
  return rewriteOrganizationWording(message);
}
